import { useId, useState, type FormEvent } from "react";
import type { BankAccount, BankAccountInput } from "@/api/settings";
import { RequestError } from "@/components/RequestError";
import { Checkbox } from "@/components/ui/checkbox";
import { TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { useFormValues, useSubmitState } from "@/lib/use-form";

export const LETTER_DATE_REQUIRED = "Enter the date the signed letter was sent.";

type BankAccountFormProps = {
  initial?: BankAccount | null;
  defaultAccountName?: string;
  submitLabel?: string;
  onSubmit: (input: BankAccountInput) => Promise<unknown>;
  onCancel?: () => void;
  onReload?: () => void;
};

export function BankAccountForm({
  initial,
  defaultAccountName = "",
  submitLabel = "Save account",
  onSubmit,
  onCancel,
  onReload,
}: BankAccountFormProps) {
  const id = useId();
  const { values, set, setValues } = useFormValues({
    label: initial?.label ?? "",
    account_name: initial?.account_name ?? defaultAccountName,
    bank: initial?.bank ?? "",
    branch: initial?.branch ?? "",
    account_no: initial?.account_no ?? "",
    swift: initial?.swift ?? "",
    ifsc: initial?.ifsc ?? "",
    iban: initial?.iban ?? "",
    currency: initial?.currency ?? "USD",
    is_default: initial?.is_default ?? false,
    confirmation_letter_sent: initial?.confirmation_letter_sent ?? false,
    letter_sent_on: initial?.letter_sent_on ?? "",
  });
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  const { error, errors: apiErrors, saving, run } = useSubmitState();
  const errors = { ...apiErrors, ...localErrors };

  function toggleLetter(checked: boolean): void {
    setValues((current) => ({
      ...current,
      confirmation_letter_sent: checked,
      letter_sent_on: checked ? current.letter_sent_on : "",
    }));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (values.confirmation_letter_sent && !values.letter_sent_on) {
      setLocalErrors({ letter_sent_on: LETTER_DATE_REQUIRED });
      return;
    }
    setLocalErrors({});
    const input: BankAccountInput = {
      label: values.label.trim(),
      account_name: values.account_name.trim(),
      bank: values.bank.trim(),
      branch: values.branch.trim(),
      account_no: values.account_no.trim(),
      swift: values.swift.trim(),
      ifsc: values.ifsc.trim(),
      iban: values.iban.replace(/\s+/g, ""),
      currency: values.currency.trim(),
      is_default: values.is_default,
      confirmation_letter_sent: values.confirmation_letter_sent,
      letter_sent_on: values.confirmation_letter_sent ? values.letter_sent_on : null,
    };
    void run(() => onSubmit(input));
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-2">
        <TextField
          id={`${id}-label`}
          label="Label"
          value={values.label}
          onChange={(value) => set("label", value)}
          error={errors.label}
          hint="How you recognise it, e.g. Federal Bank USD current account"
          className="md:col-span-2"
        />
        <TextField
          id={`${id}-account-name`}
          label="Account name"
          value={values.account_name}
          onChange={(value) => set("account_name", value)}
          error={errors.account_name}
          className="md:col-span-2"
        />
        <TextField
          id={`${id}-bank`}
          label="Bank"
          value={values.bank}
          onChange={(value) => set("bank", value)}
          error={errors.bank}
        />
        <TextField
          id={`${id}-branch`}
          label="Branch"
          value={values.branch}
          onChange={(value) => set("branch", value)}
          error={errors.branch}
        />
        <TextField
          id={`${id}-account-no`}
          label="Account number"
          inputMode="numeric"
          value={values.account_no}
          onChange={(value) => set("account_no", value)}
          error={errors.account_no}
        />
        <TextField
          id={`${id}-currency`}
          label="Currency"
          value={values.currency}
          onChange={(value) => set("currency", value)}
          error={errors.currency}
          maxLength={3}
        />
        <TextField
          id={`${id}-swift`}
          label="SWIFT / BIC"
          value={values.swift}
          onChange={(value) => set("swift", value)}
          error={errors.swift}
          hint="8 or 11 characters"
          autoCapitalize="characters"
        />
        <TextField
          id={`${id}-ifsc`}
          label="IFSC"
          value={values.ifsc}
          onChange={(value) => set("ifsc", value)}
          error={errors.ifsc}
          hint="11 characters, e.g. FDRL0001234"
          autoCapitalize="characters"
        />
        <TextField
          id={`${id}-iban`}
          label="IBAN"
          value={values.iban}
          onChange={(value) => set("iban", value)}
          error={errors.iban}
          hint="Only if the bank uses one"
          autoCapitalize="characters"
          className="md:col-span-2"
        />
      </div>
      <div className="flex flex-col gap-3">
        <Checkbox
          id={`${id}-default`}
          label="Default account for invoices"
          checked={values.is_default}
          onChange={(checked) => set("is_default", checked)}
          hint="Making this the default removes the default from any other account."
        />
        <Checkbox
          id={`${id}-letter`}
          label="Confirmation letter sent"
          checked={values.confirmation_letter_sent}
          onChange={toggleLetter}
          hint="SLA 6.4: bank details are confirmed on letterhead signed by a director."
        />
        {values.confirmation_letter_sent && (
          <TextField
            id={`${id}-letter-date`}
            label="Letter sent on"
            type="date"
            value={values.letter_sent_on}
            onChange={(value) => set("letter_sent_on", value)}
            error={errors.letter_sent_on}
            className="sm:max-w-xs"
          />
        )}
      </div>
      <FormActions saving={saving} submitLabel={submitLabel} onCancel={onCancel} />
    </form>
  );
}
